// BotDetailPage — Deep-dive into a single bot strategy
// Hero, equity curve, monthly returns, drawdown chart, trade distribution, schedule

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserAvatar, TierPill } from '../../components/Brand';
import {
  TfCard,
  TfCardList,
  TfBadge,
  TfSectionHeaderView,
  TfSparklineMarker,
  TfTimeRange,
  TfStatRow,
} from '../../components/TfPrimitives';

const BOTS: Record<string, any> = {
  breakout: {
    name: 'Gold Digger', emoji: '🥇', color: '#FF9500', author: 'Marcus Chen', authorAvatar: 'user-1',
    description: 'High-probability breakouts on GC and SI futures. Uses volume profile + ATR to filter signals.',
    category: 'Breakout', tier: 'Beginner', pairs: ['GC', 'SI'], subs: 8420, price: 0, verified: true, isHot: true,
  },
};

const RANGE_OPTIONS = ['1D', '7D', '1M', '3M', '6M', '1Y'] as const;

export function BotDetailPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const bot = BOTS[id || 'breakout'] || BOTS.breakout;
  const [subscribed, setSubscribed] = useState(false);
  const [tab, setTab] = useState<'overview' | 'trades' | 'stats' | 'reviews'>('overview');
  const [range, setRange] = useState<typeof RANGE_OPTIONS[number]>('3M');

  // 90-day equity curve
  const equity: number[] = (() => {
    const out: number[] = []; let v = 50000;
    for (let i = 0; i < 90; i++) { v += (Math.sin(i * 0.15) * 800) + (Math.random() - 0.4) * 1200; out.push(v); }
    return out;
  })();
  const ret = ((equity[equity.length - 1] - equity[0]) / equity[0]) * 100;

  // Drawdown series
  const drawdown: number[] = (() => {
    let peak = equity[0];
    return equity.map((v) => { peak = Math.max(peak, v); return ((v - peak) / peak) * 100; });
  })();
  const maxDD = Math.min(...drawdown);

  // Monthly returns
  const monthly = [
    { m: 'Jan',   ret: 8.2,  trades: 18, win: 0.72 },
    { m: 'Feb',   ret: -3.4, trades: 14, win: 0.57 },
    { m: 'Mar',   ret: 12.8, trades: 22, win: 0.68 },
    { m: 'Apr',   ret: 5.6,  trades: 16, win: 0.63 },
    { m: 'May',   ret: 18.4, trades: 28, win: 0.75 },
    { m: 'Jun',   ret: 9.7,  trades: 21, win: 0.71 },
    { m: 'Jul',   ret: 14.1, trades: 24, win: 0.73 },
  ];

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Bot</div>
        <button onClick={() => alert('Shared!')} className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3">
        <div className="rounded-[20px] text-center p-5 text-white" style={{ background: `linear-gradient(135deg, ${bot.color} 0%, ${bot.color}AA 100%)` }}>
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] text-[44px] bg-white/20" style={{ backdropFilter: 'blur(20px)' }}>{bot.emoji}</div>
          <div className="mt-2 flex items-center justify-center gap-1.5 flex-wrap">
            <h1 className="text-[22px] font-bold tracking-tight">{bot.name}</h1>
            {bot.verified && <span className="text-[14px]">✓</span>}
            {bot.isHot && <TfBadge variant="danger">Hot</TfBadge>}
          </div>
          <p className="mt-1 text-[12px] max-w-xs mx-auto text-white/90">{bot.description}</p>
          <div className="mt-2 flex items-center justify-center gap-1.5">
            <UserAvatar size={20} name={bot.author} />
            <span className="text-[12px]">by {bot.author}</span>
            <TierPill tier={bot.tier} />
          </div>
        </div>
      </div>

      {/* Subscribe CTA */}
      <div className="mx-4 mt-3 grid grid-cols-2 gap-2">
        <button
          onClick={() => nav('/portal/chart')}
          data-testid="view-chart"
          className="h-9 rounded-full bg-tf-bg border border-tf-border text-[14px] font-semibold text-tf-text-1 active:opacity-40"
        >
          Live chart
        </button>
        <button
          onClick={() => setSubscribed(!subscribed)}
          data-testid="subscribe-bot"
          className={['h-9 rounded-full font-semibold text-[14px] transition text-white active:opacity-40',
            subscribed ? 'bg-tf-profit' : 'bg-tf-text-1'
          ].join(' ')}
        >
          {subscribed ? '✓ Subscribed' : bot.price === 0 ? 'Use free' : `$${bot.price}/mo`}
        </button>
      </div>

      {/* Stats row */}
      <div className="mx-4 mt-3">
        <TfStatRow
          stats={[
            { label: 'Return', value: `+${ret.toFixed(0)}%`, color: '#34C759' },
            { label: 'Win', value: '71%' },
            { label: 'Sharpe', value: '2.41' },
          ]}
        />
      </div>

      {/* Tabs */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {(['overview', 'trades', 'stats', 'reviews'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`bottab-${t}`}
            className={['shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition capitalize',
              tab === t ? 'bg-tf-text-1 text-white' : 'bg-tf-bg border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          {/* Equity curve */}
          <TfSectionHeaderView title="90-day equity" viewAllLabel="View report" onViewAll={() => alert('Open report')} />
          <TfCard className="mx-4">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Total return</div>
                <div className="text-[24px] font-bold text-tf-profit tabular-nums">+{ret.toFixed(1)}%</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-tf-text-3">$50K →</div>
                <div className="text-[18px] font-bold tabular-nums text-tf-profit">${(equity[equity.length - 1] / 1000).toFixed(1)}K</div>
              </div>
            </div>
            <div className="-mx-1">
              <TfSparklineMarker
                points={equity}
                color="#34C759"
                height={120}
                markerIndex={Math.floor(equity.length * 0.6)}
                markerLabel={`$${(equity[Math.floor(equity.length * 0.6)] / 1000).toFixed(1)}K`}
                markerPct={((equity[Math.floor(equity.length * 0.6)] - equity[0]) / equity[0]) * 100}
              />
            </div>
            <TfTimeRange value={range} onChange={setRange} options={[...RANGE_OPTIONS]} />
          </TfCard>

          {/* Drawdown */}
          <TfSectionHeaderView title="Drawdown" />
          <TfCard className="mx-4">
            <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Max drawdown</div>
            <div className="text-[24px] font-bold text-tf-loss tabular-nums">{maxDD.toFixed(1)}%</div>
            <div className="-mx-1 mt-2">
              <TfSparklineMarker
                points={drawdown.map(d => d - maxDD)}
                color="#FF3B30"
                height={64}
                fillColor="rgba(255, 59, 48, 0.10)"
              />
            </div>
            <div className="mt-2 text-[11px] text-tf-text-3">Recovered in 4-7 days typical · Tight risk management</div>
          </TfCard>

          {/* Monthly returns */}
          <TfSectionHeaderView title="Monthly returns" viewAllLabel="Details" onViewAll={() => alert('Detailed returns')} />
          <TfCard className="mx-4 overflow-hidden">
            <div className="grid grid-cols-7 border-b border-tf-border bg-tf-surface-1 text-[10px] uppercase font-semibold text-tf-text-3">
              {monthly.map((m) => <div key={m.m} className="px-2 py-1.5 text-center">{m.m}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {monthly.map((m) => (
                <div key={m.m} data-testid={`monthly-${m.m}`} className="flex flex-col items-center justify-center border-r border-tf-border last:border-r-0 py-3">
                  <div className={['text-[12px] font-bold tabular-nums', m.ret >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>{m.ret >= 0 ? '+' : ''}{m.ret.toFixed(1)}%</div>
                  <div className="text-[9px] text-tf-text-3 tabular-nums">{(m.win * 100).toFixed(0)}%</div>
                </div>
              ))}
            </div>
          </TfCard>
        </>
      )}

      {tab === 'trades' && (
        <div className="mx-4 mt-3" data-testid="trades-list">
          <TfCardList>
            {Array.from({ length: 12 }).map((_, i) => {
              const pnl = (Math.random() - 0.3) * 200;
              const pair = ['ES', 'NQ', 'CL', 'GC'][i % 4];
              return (
                <div
                  key={i}
                  data-testid={`bot-trade-${i}`}
                  className={['flex items-center gap-3 px-4 py-3', i < 11 ? 'border-b border-tf-border' : ''].join(' ')}
                >
                  <div className={['flex h-9 w-9 items-center justify-center rounded-[10px] text-[10px] font-bold',
                    pnl > 0 ? 'bg-tf-profit/15 text-tf-profit' : 'bg-tf-loss/15 text-tf-loss'].join(' ')}>
                    {pair}
                  </div>
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-tf-text-1">{pair} · LONG</div>
                    <div className="text-[11px] text-tf-text-3">2h ago · 1h 14m hold</div>
                  </div>
                  <div className={['text-[14px] font-bold tabular-nums', pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                    {pnl >= 0 ? '+' : ''}${Math.abs(pnl).toFixed(0)}
                  </div>
                </div>
              );
            })}
          </TfCardList>
        </div>
      )}

      {tab === 'stats' && (
        <div className="mx-4 mt-3 space-y-2">
          <StatSection title="Performance" stats={[
            { l: 'Total return',   v: `+${ret.toFixed(1)}%`,     color: 'text-tf-profit' },
            { l: 'Annualized',     v: '+89.4%',                  color: 'text-tf-profit' },
            { l: 'Win rate',       v: '71%' },
            { l: 'Profit factor',  v: '2.41' },
            { l: 'Sharpe ratio',   v: '2.18' },
            { l: 'Sortino ratio',  v: '3.12' },
            { l: 'CAGR',           v: '+94.7%',                  color: 'text-tf-profit' },
          ]} />
          <StatSection title="Risk" stats={[
            { l: 'Max drawdown',   v: `${maxDD.toFixed(1)}%`,    color: 'text-tf-loss' },
            { l: 'Avg drawdown',   v: '-2.4%' },
            { l: 'Recovery time',  v: '4.2 days' },
            { l: 'Volatility',     v: '8.2%' },
            { l: 'Beta (vs ES)',   v: '0.18' },
          ]} />
          <StatSection title="Activity" stats={[
            { l: 'Total trades',   v: '412' },
            { l: 'Avg hold time',  v: '1h 24m' },
            { l: 'Trades per day', v: '4.6' },
            { l: 'Best day',       v: '+$1,280',                 color: 'text-tf-profit' },
            { l: 'Worst day',      v: '-$340',                   color: 'text-tf-loss' },
            { l: 'Longest streak', v: '12 wins' },
          ]} />
        </div>
      )}

      {tab === 'reviews' && (
        <div className="mx-4 mt-3 space-y-2">
          {[
            { name: 'Sofia Petrov',  avatar: 'user-2', rating: 5, time: '2d ago', text: 'Best breakout bot I\'ve used. Consistent results, low drawdowns.' },
            { name: 'Aiden Wright',  avatar: 'user-3', rating: 4, time: '1w ago', text: 'Solid strategy. The free tier is generous, paid is even better.' },
            { name: 'Lena Voss',     avatar: 'user-4', rating: 5, time: '2w ago', text: '83% win rate over 50 trades for me. Worth every cent.' },
            { name: 'Yuki Tanaka',   avatar: 'user-4', rating: 5, time: '3w ago', text: 'Plug and play. Set it up in 5 minutes, made $800 in week 1.' },
          ].map((r, i) => (
            <TfCard key={i} className="p-3" testId={`review-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <UserAvatar size={32} name={r.name} />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-tf-text-1">{r.name}</div>
                  <div className="text-[10px] text-tf-text-3">{r.time}</div>
                </div>
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <svg key={j} viewBox="0 0 24 24" className={['h-3.5 w-3.5', j < r.rating ? 'text-tf-warning' : 'text-[#E5E5EA]'].join(' ')} fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
              </div>
              <p className="text-[13px] text-tf-text-1">{r.text}</p>
            </TfCard>
          ))}
        </div>
      )}
    </div>
  );
}

function StatSection({ title, stats }: { title: string; stats: { l: string; v: string; color?: string }[] }) {
  return (
    <TfCard className="overflow-hidden">
      <div className="border-b border-tf-border bg-tf-surface-1 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-tf-text-3">{title}</div>
      {stats.map((s, i) => (
        <div key={s.l} className={['flex items-center justify-between px-4 py-2.5', i < stats.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
          <span className="text-[12px] text-tf-text-3">{s.l}</span>
          <span className={['text-[14px] font-semibold tabular-nums', s.color || 'text-tf-text-1'].join(' ')}>{s.v}</span>
        </div>
      ))}
    </TfCard>
  );
}
